import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Edit, Trash2, Clock } from 'lucide-react'
import { AlertModal } from '@/common/components/AlertModal'
import {
	useMutationDeleteSchedule,
	useMutationUpdateSchedule,
} from '../../api/hooks'
import { transactionSettingsKeys } from '../../api/keys'
import type { TransactionSchedule, TransactionType } from '../../types'

interface ScheduleTableProps {
	schedules: TransactionSchedule[]
	type: TransactionType
	isLoading?: boolean
	currentActiveSchedule?: TransactionSchedule
}

export const ScheduleTable = ({
	schedules,
	type,
	isLoading,
	currentActiveSchedule,
}: ScheduleTableProps) => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
		null,
	)

	const typeLabel = type === 'deposit' ? 'ฝากเงิน' : 'ถอนเงิน'

	const updateMutation = useMutationUpdateSchedule({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.schedules(type),
			})
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.status(type),
			})
			toast.success('อัปเดตตารางเวลาสำเร็จ')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตตารางเวลา')
		},
	})

	const deleteMutation = useMutationDeleteSchedule({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.schedules(type),
			})
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.status(type),
			})
			toast.success('ลบตารางเวลาสำเร็จ')
			setDeleteModalOpen(false)
			setSelectedScheduleId(null)
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการลบตารางเวลา')
		},
	})

	const handleToggleActive = (schedule: TransactionSchedule) => {
		updateMutation.mutate({
			id: schedule.id,
			type,
			data: { isActive: !schedule.isActive },
		})
	}

	const handleDelete = () => {
		if (!selectedScheduleId) return
		deleteMutation.mutate({ id: selectedScheduleId, type })
	}

	const handleEdit = (id: string) => {
		navigate(`/operator/transaction-settings/schedules/edit/${id}?type=${type}`)
	}

	/**
	 * Convert UTC HH:mm to local timezone display
	 * Uses browser's timezone automatically
	 */
	const formatTimeDisplay = (timeStr: string) => {
		const [hours, minutes] = timeStr.split(':').map(Number)

		// Create a date in UTC
		const utcDate = new Date()
		utcDate.setUTCHours(hours, minutes, 0, 0)

		// Get local hours/minutes from the same moment
		const localHours = utcDate.getHours()
		const localMinutes = utcDate.getMinutes()

		return `${localHours.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')} น.`
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				{[...Array(3)].map((_, i) => (
					<Skeleton key={i} className="h-12 w-full" />
				))}
			</div>
		)
	}

	return (
		<>
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>เวลาเริ่มปิด</TableHead>
							<TableHead>เวลาเปิด</TableHead>
							<TableHead>สถานะ</TableHead>
							<TableHead>สร้างเมื่อ</TableHead>
							<TableHead className="text-right">จัดการ</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{schedules.length > 0 ? (
							schedules.map((schedule) => {
								const isCurrentlyActive =
									currentActiveSchedule?.id === schedule.id

								return (
									<TableRow
										key={schedule.id}
										className={
											isCurrentlyActive ? 'bg-red-50 dark:bg-red-950/20' : ''
										}
									>
										<TableCell className="font-medium">
											<div className="flex items-center gap-2">
												<Clock className="h-4 w-4 text-muted-foreground" />
												{formatTimeDisplay(schedule.inactiveStartAt)}
											</div>
										</TableCell>
										<TableCell>
											{formatTimeDisplay(schedule.inactiveEndAt)}
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Switch
													checked={schedule.isActive}
													onCheckedChange={() => handleToggleActive(schedule)}
													disabled={updateMutation.isPending}
												/>
												{isCurrentlyActive && (
													<Badge variant="destructive">กำลังใช้งาน</Badge>
												)}
											</div>
										</TableCell>
										<TableCell>
											{format(
												new Date(schedule.createdAt),
												'dd MMM yyyy HH:mm',
												{ locale: th },
											)}
										</TableCell>
										<TableCell className="text-right">
											<div className="flex justify-end gap-2">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleEdit(schedule.id)}
												>
													<Edit className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => {
														setSelectedScheduleId(schedule.id)
														setDeleteModalOpen(true)
													}}
													disabled={deleteMutation.isPending}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								)
							})
						) : (
							<TableRow>
								<TableCell colSpan={5} className="text-center">
									<div className="py-8 text-muted-foreground">
										ยังไม่มีตารางเวลาปิด{typeLabel}
									</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			<AlertModal
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false)
					setSelectedScheduleId(null)
				}}
				onConfirm={handleDelete}
				loading={deleteMutation.isPending}
			/>
		</>
	)
}
